import { ColDef } from 'ag-grid';
import {
  isEmpty as _isEmpty,
  isArray as _isArray,
  filter as _filter,
  some as _some,
  find as _find,
  map as _map,
  get as _get,
} from 'lodash';

import { DateEra, DateSeparator, DateTimeOutput, DateTimePadding, DateTimeUtils, StringUtils } from '@blcloud/bl-common';

import { IItemSettingInfo } from '@blcloud/bl-ng-share-module';
import { TooltipCellComponent, TooltipFilterCellComponent } from '@blcloud/bl-ng-ui-component';
import { LabelViewComponent, CustomerVehicleSearchLabel } from '@blcloud/bl-ng-vehicle-customer-module';

/** 画面タイトル */
export const PAGE_TITLE = '受付一覧';

/** グリッドサイズパターン */
export const PageSizePatterns = [50, 100, 150, 200];

/** ローカルストレージキー */
export const SEARCH_CONDITION_STORAGE_KEY = 'ReceptList_SearchCondition';

/** 検索最大件数 */
export const SEARCH_MAX_COUNT = 1000;

/** 設定識別ID　検索条件 */
export const SETTING_ID_CONDITIONS = 'search-conditions';

/** 設定識別ID　グリッド列 */
export const SETTING_ID_COLUMNS = 'search-columns';

/** バブルファンクションロール定義 */
export const BubbleFnRole = {
  /** 列表示設定 */
  ColumnSetting: 2000,
};

/** メッセージ定義 */
export const Message = {
  /** 検索件数オーバー */
  OverSearchCount:
    '検索結果が' + SEARCH_MAX_COUNT + '件を超えています。<br>' + SEARCH_MAX_COUNT + '件以下となるように検索条件を指定してください。',
  /** 条件未入力 */
  EmptyCondition: '検索内容を入力してください。新規に受付を作成することもできます。',
  /** 該当なし */
  NotFound: '該当する候補が見つかりません。内容を確認し再検索するか、新規に受付を作成してください。',
  /** 検索エラー */
  SearchError:
    '検索に失敗しました。時間をおいて再度お試しください。<br>検索対象が多い条件で検索している場合は、検索条件にてさらに絞り込みを行い再度お試しください。',
};

/** 検索条件ID定義 */
export const ConditionId = {
  /** 案件タイトル */
  caseTitle: 'caseTitle',
  /** 日付範囲 */
  caseDate: 'caseDate',
  /** 案件終了日 */
  caseEndDate: 'caseEndDate',
  /** 日時選択リスト */
  // reserveSearchDateType: 'reserveSearchDateType',
  /** プレートNo */
  numPlateSeriesSpecifNum: 'numPlateSeriesSpecifNum',
  /** 顧客フリガナ */
  customerNameKana: 'customerNameKana',
  /** 受付状況 */
  bizNegoStatusList: 'bizNegoStatusList',
  /** 受付管理組織 */
  organizationCode: 'organizationCode',
  /** サービス */
  itemServiceNum: 'itemServiceNum',
  /** 受付担当 */
  receptPicCode: 'receptPicCode',
  /** 受付ラベル */
  labelCodeList: 'labelCodeList',
  /** 受付番号　非表示 */
  // receptionNum: 'receptionNum',
  /** 案件種別 */
  caseTypeCode: 'caseTypeCode',
  /** 最新承認 */
  // latestApproval: 'latestApproval',
  /** 取引関係者 */
  transactionRelatedPerson: 'transactionRelatedPerson',
  /** 取引工程 */
  transactionProcess: 'transactionProcess',
  /** 証跡有無 */
  evidenceExistence: 'evidenceExistence',
  /** 案件認証システムコード */
  caseAuthCode: 'caseAuthCode',
  /** 案件店舗名称 */
  caseShopName: 'caseShopName',
  /** 案件連携システムコード */
  caseLinkedSystemCode: 'caseLinkedSystemCode',
  /** 日付 */
  date: 'date',
};

/** ベースとなる検索条件設定の定義 */
export const BaseConditionSettingInfo: IItemSettingInfo = {
  title: '検索条件設定',
  splitFlag: true,
  mainItemTitle: '基本条件',
  mainItems: [
    { itemId: ConditionId.date, itemName: '日付', itemDispFlag: true },
    { itemId: ConditionId.numPlateSeriesSpecifNum, itemName: 'プレートNo', itemDispFlag: true },
    { itemId: ConditionId.customerNameKana, itemName: '顧客フリガナ', itemDispFlag: true },
    // 以下の項目は受付済専用項目のため2段目に表示する。
    { itemId: ConditionId.bizNegoStatusList, itemName: '受付状況', itemDispFlag: true },
  ],
  subItemTitle: '詳細条件',
  subItems: [
    { itemId: ConditionId.organizationCode, itemName: '受付組織', itemDispFlag: true },
    { itemId: ConditionId.itemServiceNum, itemName: 'サービス', itemDispFlag: true },
    { itemId: ConditionId.receptPicCode, itemName: '受付担当', itemDispFlag: true },
    // { itemId: ConditionId.receptionNum, itemName: '受付番号', itemDispFlag: true },
    { itemId: ConditionId.labelCodeList, itemName: '受付ラベル', itemDispFlag: true },
    // { itemId: ConditionId.caseTypeCode, itemName: '案件種別', itemDispFlag: true },
    // { itemId: ConditionId.caseTitle, itemName: '案件タイトル', itemDispFlag: true },
    // { itemId: ConditionId.transactionRelatedPerson, itemName: '取引関係者', itemDispFlag: true },
    // { itemId: ConditionId.transactionProcess, itemName: '取引工程', itemDispFlag: true },
    // { itemId: ConditionId.evidenceExistence, itemName: '証跡有無', itemDispFlag: true },
  ],
};

/** 列ID定義 */
export const ColumnId = {
  /** 受付状況 */
  bizNegoStatus: 'bizNegoInfo.bizNegoStatusEnumName',
  /** 受付日 */
  receptionDate: 'receptionInfo.receptionDate',
  /** 受付番号 */
  // receptionNum: 'displayReceptionNum',
  /** 顧客コード */
  customerCode: 'receptionInfo.customerCode',
  /** お客様名 */
  customerName: 'customerName',
  /** フリガナ */
  customerNameKana: 'customerNameKana',
  /** 連絡先 */
  contactInfo: 'receptionInfo.contactInfoTel.contactInformation',
  /** 車両登録番号 */
  plateNo: 'plateNo',
  /** メーカー */
  vehicleName: 'vehicleInfo.vehicleInspCertifInfo.vehicleName',
  /** 車種 */
  vehicleModelFixFullWName: 'vehicleInfo.vehicleSpecificationInfo.vehicleModelFixFullWName',
  /** サービス */
  itemServiceName: 'bizNegoInfo.itemServiceName',
  /** 受付担当 */
  receptPicName: 'receptionInfo.receptPicName',
  /** 受付管理組織 */
  receptOrganizationName: 'receptOrganizationName',
  /** 受付ラベル */
  labelList: 'labelList',
  /** 予約ステータス */
  reservationStatus: 'receptionInfo.reservationStatus',
  /** 希望日時 */
  visitStartDateTime: 'visitStartDateTime',
  /** 入庫日時 */
  itemServiceOfferStartDateTime: 'bizNegoInfo.itemServiceOfferStartDateTime',
  /** 納車日時 */
  vehicleDeliveryStartTime: 'vehicleDeliveryStartTime',
  /** 引取日時 */
  takeOverDateTime: 'takeOverDateTime',
  /** 案件種別 */
  caseTypeCode: 'caseInfoDisplay.caseTypeCodeEnumName',
  /** 案件タイトル */
  caseTitle: 'caseInfoDisplay.caseTitle',
  /** 最新承認 */
  // latestApproval: 'receptionInfo.latestApproval',
  /** 取引関係者 */
  transactionRelatedPerson: 'receptionInfo.transactionRelatedPerson',
  /** 取引工程 */
  transactionProcess: 'receptionInfo.transactionProcess',
  /** 証跡 */
  existEvidence: 'receptionInfo.existEvidence',
  /** 案件有無 */
  existCase: 'existCase',
  /** 代車利用 */
  replacementVehicle: 'replacementVehicle',
};

/** デフォルト列定義 */
export const DefaultColumns = <ColDef[]>[
  {
    headerName: '受付組織',
    field: ColumnId.receptOrganizationName,
    width: 120,
    valueGetter: (params) => ({
      value: _get(params.data, ColumnId.receptOrganizationName),
      tooltip: _get(params.data, ColumnId.receptOrganizationName),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  // {
  //   headerName: '受付番号',
  //   field: ColumnId.receptionNum,
  //   width: 80,
  // },
  {
    headerName: '受付状況',
    field: ColumnId.bizNegoStatus,
    width: 100,
    valueGetter: (params) => ({
      value: _get(params.data, ColumnId.bizNegoStatus),
      tooltip: _get(params.data, ColumnId.bizNegoStatus),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  {
    headerName: 'サービス',
    field: ColumnId.itemServiceName,
    width: 120,
    valueGetter: (params) => ({
      value: _get(params.data, ColumnId.itemServiceName),
      tooltip: _get(params.data, ColumnId.itemServiceName),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  {
    headerName: '顧客名称',
    field: ColumnId.customerName,
    width: 200,
    valueGetter: (params) => ({
      value: _get(params.data, ColumnId.customerName),
      tooltip: _get(params.data, ColumnId.customerName),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  {
    headerName: 'フリガナ',
    field: ColumnId.customerNameKana,
    width: 200,
    valueGetter: (params) => ({
      value: _get(params.data, ColumnId.customerNameKana),
      tooltip: _get(params.data, ColumnId.customerNameKana),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  {
    headerName: '車両登録番号',
    field: ColumnId.plateNo,
    width: 160,

    valueGetter: (params) => ({
      value: _get(params.data, ColumnId.plateNo),
      tooltip: _get(params.data, ColumnId.plateNo),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    // cellRendererFramework: NumberPlateViewComponent,
    suppressMovable: false,
    comparator: numberPlateComparator,
  },
  {
    headerName: 'メーカー',
    field: ColumnId.vehicleName,
    width: 120,
    valueGetter: (params) => ({
      value: _get(params.data, ColumnId.vehicleName),
      tooltip: _get(params.data, ColumnId.vehicleName),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  {
    headerName: '車種',
    field: ColumnId.vehicleModelFixFullWName,
    width: 130,
    valueGetter: (params) => ({
      value: _get(params.data, ColumnId.vehicleModelFixFullWName),
      tooltip: _get(params.data, ColumnId.vehicleModelFixFullWName),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  {
    headerName: '受付日',
    field: ColumnId.receptionDate,
    // valueFormatter: dateFormatter,
    width: 130,
    valueGetter: (params) => {
      const value = dateFormatter(_get(params.data, ColumnId.receptionDate));
      return { value: value, tooltip: value, placement: 'bottom' };
    },
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  {
    headerName: '希望日時',
    field: ColumnId.visitStartDateTime,
    // valueFormatter: dateTimeValueFormatter,
    width: 170,
    valueGetter: (params) => ({
      value: dateTimeValueFormatter(_get(params.data, ColumnId.visitStartDateTime)),
      tooltip: dateTimeValueFormatter(_get(params.data, ColumnId.visitStartDateTime)),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  {
    headerName: '入庫日時',
    field: ColumnId.itemServiceOfferStartDateTime,
    // valueFormatter: dateTimeValueFormatter,
    width: 170,
    valueGetter: (params) => ({
      value: dateTimeValueFormatter(_get(params.data, ColumnId.itemServiceOfferStartDateTime)),
      tooltip: dateTimeValueFormatter(_get(params.data, ColumnId.itemServiceOfferStartDateTime)),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  {
    headerName: '引取日時',
    field: ColumnId.takeOverDateTime,
    width: 170,
    valueGetter: (params) => ({
      value: dateTimeValueFormatter(_get(params.data, ColumnId.takeOverDateTime)),
      tooltip: dateTimeValueFormatter(_get(params.data, ColumnId.takeOverDateTime)),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  {
    headerName: '納車日時',
    field: ColumnId.vehicleDeliveryStartTime,
    // valueFormatter: dateTimeValueFormatter,
    width: 170,
    valueGetter: (params) => ({
      value: dateTimeValueFormatter(_get(params.data, ColumnId.vehicleDeliveryStartTime)),
      tooltip: dateTimeValueFormatter(_get(params.data, ColumnId.vehicleDeliveryStartTime)),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  {
    headerName: '受付担当',
    field: ColumnId.receptPicName,
    width: 120,
    valueGetter: (params) => ({
      value: _get(params.data, ColumnId.receptPicName),
      tooltip: _get(params.data, ColumnId.receptPicName),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  {
    headerName: '顧客コード',
    field: ColumnId.customerCode,
    width: 100,
    valueGetter: (params) => ({
      value: _get(params.data, ColumnId.customerCode) === 0 ? '' : _get(params.data, ColumnId.customerCode).toString(),
      tooltip: _get(params.data, ColumnId.customerCode) === 0 ? '' : _get(params.data, ColumnId.customerCode).toString(),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparatorCodeValue,
  },
  {
    headerName: '連絡先',
    field: ColumnId.contactInfo,
    width: 120,
    valueGetter: (params) => ({
      value: _get(params.data, ColumnId.contactInfo),
      tooltip: _get(params.data, ColumnId.contactInfo),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  {
    headerName: '受付ラベル',
    field: ColumnId.labelList,
    cellRendererFramework: LabelViewComponent,
    comparator: labelComparator,
    width: 120,
    suppressMovable: false,
  },
  // 案件有無
  {
    headerName: '案件管理',
    field: ColumnId.existCase,
    width: 100,
    align: 'center',
    valueGetter: (params) => ({
      value: _get(params.data, ColumnId.existCase),
      tooltip: _get(params.data, ColumnId.existCase),
      placement: 'bottom',
    }),
    cellRendererFramework: TooltipCellComponent,
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  {
    headerName: '代車利用',
    field: ColumnId.replacementVehicle,
    width: 180,
    valueGetter: (params) => _get(params.data, ColumnId.replacementVehicle),
    cellRendererFramework: TooltipFilterCellComponent,
    cellRendererParams: {
      placement: 'bottom',
      getTooltip: (params) => {
        const val = params.value;
        if (!val || val === '無し') {
          return '';
        }
        return val;
      }
    },
    suppressMovable: false,
    comparator: tooltipComparator,
  },
  // {
  //   headerName: '案件種別',
  //   field: ColumnId.caseTypeCode,
  //   width: 100,
  // },
  // 2025/10時点ではサービスと同じ名前のため非表示
  // {
  //   headerName: '案件タイトル',
  //   field: ColumnId.caseTitle,
  //   width: 150,
  // },
  // 最新承認は負荷の観点から非表示
  // {
  //   headerName: '最新承認',
  //   field: ColumnId.latestApproval,
  //   width: 100,
  // },
  // {
  //   headerName: '取引関係者',
  //   field: ColumnId.transactionRelatedPerson,
  //   width: 120,
  // },
  // {
  //   headerName: '案件進捗',
  //   field: ColumnId.transactionProcess,
  //   width: 100,
  // },
  // {
  //   headerName: '証跡',
  //   field: ColumnId.existEvidence,
  //   width: 100,
  // },
];

/** 日付フォーマッタ */
function dateFormatter(value: string) {
  if (_isEmpty(value) || value === DateTimeUtils.initial.iso8601.date) {
    return '';
  }
  return DateTimeUtils.format(value, DateTimeOutput.YMD, DateTimePadding.ZERO, DateSeparator.JP, DateEra.JP);
}

function dateTimeValueFormatter(value) {
  if (!_isEmpty(value)) {
    return DateTimeUtils.format(DateTimeUtils.parseDate(value), DateTimeOutput.YMD_LT, DateTimePadding.ZERO, DateSeparator.JP, DateEra.JP);
  } else {
    return '';
  }
}

/**
 * ラベル比較
 * @param objA 比較対象A
 * @param objB 比較対象B
 * @returns 比較結果
 */
function labelComparator(objA: CustomerVehicleSearchLabel, objB: CustomerVehicleSearchLabel): number {
  const toText = (obj: CustomerVehicleSearchLabel): string => {
    if (_isEmpty(obj) || !_isArray(obj.value)) {
      return '';
    }
    const labelCodeList = obj.value;
    const labelUseCategory = obj.labelUseCategory;
    const masterLabelList = obj.master;

    const filterLabelList = _filter(masterLabelList, (label) => {
      return _some(
        labelCodeList,
        (code) => code === label.labelCode && !!_find(label.labelUseCategoryList, (category) => category === labelUseCategory)
      );
    });

    const mapLabelList = _map(filterLabelList, (label) => label.labelName);
    return StringUtils.concatWith('／', ...mapLabelList);
  };

  const textA = toText(objA);
  const textB = toText(objB);

  return textA.localeCompare(textB, 'jp');
}

/**
 * 汎用ツールチップ表示項目用Comparator
 */
function tooltipComparator(valueA: any | null, valueB: any | null): number {
  const textA = (valueA != null && typeof valueA === 'object') ? (valueA['value'] || '') : (valueA || '');
  const textB = (valueB != null && typeof valueB === 'object') ? (valueB['value'] || '') : (valueB || '');

  if (textA === textB) {
    return 0;
  } else {
    return textA > textB ? 1 : -1;
  }
}

/**
 * コードセルcomparator
 * @param valueA 比較対象A
 * @param valueB 比較対象B
 * @returns 判定結果
 */
function tooltipComparatorCodeValue(A: any, B: any): number {
  const valueA = A && A['value'] ? A['value'] : 0;
  const valueB = B && B['value'] ? B['value'] : 0;

  const numA = parseInt(valueA, 10);
  const numB = parseInt(valueB, 10);

  const validNumA = isNaN(numA) ? 0 : numA;
  const validNumB = isNaN(numB) ? 0 : numB;

  if (validNumA === validNumB) {
    return 0;
  } else {
    return validNumA > validNumB ? 1 : -1;
  }
}

/**
 * ナンバープレート比較
 * @param objA 比較対象A
 * @param objB 比較対象B
 * @returns 比較結果
 */
// NOTE: 元々の実装がイマイチで型定義されていないので仕方なくそのままany
function numberPlateComparator(A: any, B: any): number {
  const textA = A && A['value'] ? A['value'] : '';
  const textB = B && B['value'] ? B['value'] : '';

  if (textA === textB) {
    return 0;
  } else if (_isEmpty(textA)) {
    return -1;
  } else if (_isEmpty(textB)) {
    return 1;
  } else if (textA === '車両なし') {
    return -1;
  } else if (textB === '車両なし') {
    return 1;
  } else if (textA === '仮登録車両') {
    return -1;
  } else if (textB === '仮登録車両') {
    return 1;
  } else {
    return textA.localeCompare(textB, 'jp');
  }
}



// WEBPACK FOOTER //
// ./src/app/page/recept-list-page/recept-list/recept-list.define.ts